<template>
	<div class="metric-box">
		<div>
			<slot></slot>
		</div>
		<div v-if="view === 'metric'">
			<h3 v-if="type == 'money'" class="is-inline-block">
				{{ $filters.Currency($store.getters.user.Currency, current) }}
			</h3>
			<h3 v-else class="is-inline-block">{{ nformat(current) }}</h3>
			<span v-if="compare.length">
				<o-icon icon="long-arrow-up" pack="fas" class="success" v-if="change > 0"></o-icon>
				<o-icon icon="minus" pack="fas" class="neutral" v-if="change == 0"></o-icon>
				<o-icon icon="long-arrow-down" pack="fas" class="danger" v-if="change < 0"></o-icon>
				<span :class="{ success: change > 0, neutral: change == 0, danger: change < 0 }">
					{{ $filters.Percentage(change) }}
				</span>
			</span>
		</div>
		<div class="is-flex is-justify-content-space-between" v-if="view === 'plain'">
			<div>
				<slot name="description"></slot>
			</div>
			<div>
				<o-icon icon="long-arrow-up" pack="fas" class="success" v-if="change > 0"></o-icon>
				<o-icon icon="minus" pack="fas" class="neutral" v-if="change == 0"></o-icon>
				<o-icon icon="long-arrow-down" pack="fas" class="danger" v-if="change < 0"></o-icon>
				{{ nformat(current - previous) }}
				<slot name="descriptor">Items</slot>
			</div>
		</div>
		<div v-if="metric">
			<kpi :samples="samples" :metric="metric" :config="{ segment: includeSegment }"></kpi>
		</div>
	</div>
</template>
<script>
	import { ref, computed, onMounted, inject } from "vue";
	import { currencyFormat, numberFormat } from "@/services/utils";
	import kpi from "./kpiFacade.vue";

	export default {
		components: {
			kpi
		},
		props: {
			metric: {
				type: String,
				default: ""
			},
			results: {
				type: Array,
				default: () => {
					return [];
				}
			},
			compare: {
				type: Array,
				default: () => {
					return [];
				}
			},
			samples: {
				type: Object,
				default: () => {
					return {};
				}
			},
			type: {
				type: String,
				default: "money"
			},
			view: {
				type: String,
				default: "metric"
			},
			includeSegment: {
				type: Boolean,
				default: true
			}
		},
		setup(props, context) {
			const $financial = inject("$financial");

			const cformat = ref(currencyFormat.format);
			const nformat = ref(numberFormat.format);
			const pformat = ref(numberFormat.percent);

			const sample = computed(() => {
				return props.samples[props.metric];
			});

			const getFilledJobValue = (rows) => {
				if (!rows || rows.length == 0) return 0;

				const total = rows.reduce((acc, row) => {
					return acc + row[props.metric];
				}, 0);
				const weightedTotal = rows.reduce((acc, row) => {
					return acc + (row.filledJobValueTotal || 0);
				}, 0);
				const weightedCount = rows.reduce((acc, row) => {
					return acc + (row.filledJobValueCount || 0);
				}, 0);

				if (weightedCount > 0) {
					return weightedTotal / weightedCount;
				}

				return total / (rows.filter((r) => r[props.metric] != 0).length || 1);
			};

			const current = computed(() => {
				if (props.results.length == 0) return 0;

				let total = 0;
				if (props.metric == "teamingValue") return props.results[0]["teamingValue_metric"];
				if (props.metric == "filledJobValue") return getFilledJobValue(props.results);

				total = props.results.reduce((acc, row) => {
					return acc + row[props.metric];
				}, 0);

				return total || 0;
			});

			const previous = computed(() => {
				if (props.compare.length == 0) return 0;

				let total = 0;
				if (props.metric == "teamingValue") return props.compare[0]["teamingValue_metric"];
				if (props.metric == "filledJobValue") return getFilledJobValue(props.compare);

				total = props.compare.reduce((acc, row) => {
					return acc + row[props.metric];
				}, 0);

				return total || 0;
			});

			const change = computed(() => {
				if (current.value === 0 && previous.value === 0) {
					return 0;
				} else if (previous.value === 0) {
					return 1;
				} else if (current.value === 0) {
					return previous.value > 0 ? -1 : 1;
				}

				return (current.value - previous.value) / Math.abs(previous.value);
			});

			return {
				cformat,
				nformat,
				pformat,
				sample,
				current,
				previous,
				change
			};
		}
	};
</script>
<style lang="scss" scoped>
	@import "@/assets/css/globalVars.scss";

	.metric-box {
		& .success {
			color: $boyden-green;
		}
		& .danger {
			color: $boyden-red;
		}
		& .neutral {
			color: #ddd;
		}
	}
</style>
